/** Schémas zod des routes d'administration (§6). */
import { z } from 'zod';
import { CLOUDRON_MIN_MEMORY_MB, QUOTA_KEYS } from '../constants.js';
import { DEPLOYMENT_STATES } from '../state-machine/states.js';
import { APP_STATUSES } from '../types/dto.js';
import { paginationQuerySchema } from './deployment.js';
/**
 * Booléen transmis en paramètre d'URL.
 *
 * `z.coerce.boolean()` ne convient pas ici : il applique la conversion
 * JavaScript, pour laquelle toute chaîne non vide vaut `true` — y compris
 * « false ». Une case décochée arriverait donc cochée côté serveur.
 */
const queryBoolean = z
    .union([z.boolean(), z.string()])
    .transform((value) => typeof value === 'boolean' ? value : ['1', 'true', 'yes', 'oui'].includes(value.toLowerCase()));
/** Filtres de l'inventaire des applications (§6.2). */
export const adminAppsQuerySchema = paginationQuerySchema.extend({
    search: z.string().max(120).optional(),
    status: z.enum(APP_STATUSES).optional(),
    ownerId: z.string().optional(),
    domain: z.string().optional(),
    createdAfter: z.string().datetime().optional(),
    createdBefore: z.string().datetime().optional(),
    includeDeleted: queryBoolean.default(false),
    /**
     * Ne rend que les applications supprimées.
     *
     * C'est une vue à part entière, et non un filtre parmi d'autres : ce qu'on y
     * fait — restaurer, effacer définitivement — n'a de sens que là. Distinct de
     * `includeDeleted`, qui mêle les vivantes et les mortes dans l'inventaire.
     */
    onlyDeleted: queryBoolean.default(false),
    /**
     * Ne rend que les applications dont la suppression est demandée.
     *
     * C'est ce que désigne la pastille de notification : sans ce filtre, elle
     * renverrait vers un inventaire où la demande peut se trouver page trois.
     */
    pendingDeletion: queryBoolean.default(false),
    sortBy: z.enum(['createdAt', 'slug', 'lastDeployedAt', 'visits7d']).default('createdAt'),
    sortDir: z.enum(['asc', 'desc']).default('desc'),
});
/** Filtres de la liste de tous les déploiements. */
export const adminDeploymentsQuerySchema = paginationQuerySchema.extend({
    state: z.enum(DEPLOYMENT_STATES).optional(),
    userId: z.string().optional(),
    appId: z.string().optional(),
    search: z.string().max(120).optional(),
});
/** Filtres du journal d'audit (§6.4). */
export const adminAuditQuerySchema = paginationQuerySchema.extend({
    actorId: z.string().optional(),
    action: z.string().max(120).optional(),
    targetType: z.string().max(60).optional(),
    targetId: z.string().max(120).optional(),
    from: z.string().datetime().optional(),
    to: z.string().datetime().optional(),
});
/** Confirmation double exigée pour la suppression d'une application (§6.3). */
export const adminDeleteAppSchema = z.object({
    /** L'administrateur doit ressaisir le slug exact de l'application. */
    confirmSlug: z.string().min(1),
});
/**
 * Refus d'une demande de suppression, avec son motif.
 *
 * Le motif est **obligatoire**, à la différence de la demande elle-même : refuser
 * sans rien dire laisse l'auteur devant une application qu'il voulait supprimer,
 * sans savoir quoi faire, et la demande repart le lendemain.
 */
export const adminRefuseDeletionSchema = z.object({
    reason: z
        .string({ error: 'Indiquez le motif du refus : il est affiché au demandeur.' })
        .trim()
        .min(3, 'Indiquez le motif du refus : il est affiché au demandeur.')
        .max(500),
});
/** Modification des réglages d'une application (§6.3). */
export const adminUpdateAppSchema = z
    .object({
    // Le minimum est celui de la plateforme, et non 128 Mo : en deçà, Cloudron
    // refuse l'installation avec « memoryLimit too small », au terme du pipeline.
    memoryLimitMb: z.number().int().min(CLOUDRON_MIN_MEMORY_MB).max(4096).optional(),
    claudeProxyEnabled: z.boolean().optional(),
})
    .refine((value) => Object.keys(value).length > 0, {
    message: 'Aucune modification fournie.',
});
/** Mise à jour des quotas, globaux ou par utilisateur (§6.5). */
export const adminQuotaUpdateSchema = z.object({
    scope: z.enum(['global', 'user']),
    /** Obligatoire lorsque `scope` vaut `user`. */
    userId: z.string().nullish(),
    key: z.enum(QUOTA_KEYS),
    /** `null` retire la surcharge et rétablit la valeur héritée. */
    value: z.number().int().min(0).nullable(),
});
export const adminQuotasUpdateSchema = z.object({
    updates: z.array(adminQuotaUpdateSchema).min(1).max(50),
});
/** Périodes de consultation des statistiques de visites. */
export const STATS_PERIODS = ['24h', '7d', '30d', '90d'];
export const statsQuerySchema = z.object({
    period: z.enum(STATS_PERIODS).default('7d'),
});
/** Export de données (§6.2, §6.4 et `GET /api/admin/export`). */
export const adminExportQuerySchema = z.object({
    dataset: z.enum(['apps', 'deployments', 'audit']),
    format: z.enum(['csv', 'json']).default('csv'),
});
//# sourceMappingURL=admin.js.map